import { z } from 'zod';

import { authSessionResponseSchema, meResponseSchema, type AuthenticatedUser } from '@emr/shared';

import { apiRequest } from './client.ts';

/**
 * The authentication surface, validated against `@emr/shared`'s own response schemas rather than a
 * locally-invented approximation of them.
 *
 * **This file previously defined its own `sessionSchema`**, written speculatively before the real
 * server endpoints existed (`BACKLOG.md` O1–O3), with a shape nobody reconciled against the real one
 * once `shared/src/authRequests.ts`'s `authenticatedUserSchema` was actually built — a nested
 * `user`/`org` split with a `displayName` field that has no server-side backing at all. It looked
 * internally consistent and passed every test written against it, because nothing had ever driven a
 * real login through both sides wired together — found only once a real browser hit a real deployed
 * server for the first time and `apiRequest` correctly refused to accept a response that did not
 * match its own schema. Importing the shared schema directly is what keeps this from recurring: one
 * definition, not two that can drift.
 *
 * `Session` is `AuthenticatedUser`'s flat shape (`userId`, `orgName`, `tier`, ... — not nested `user`/
 * `org` objects), used uniformly by every consumer in this app regardless of which endpoint supplied
 * it. `role` is `string`, not `string | null`, matching `authenticatedUserSchema`: the server never
 * constructs one for an identity with no role in the org (see `describeUser` in
 * `server/src/auth/service.ts`), so by the time a `Session` exists, `role` already is one.
 */

export type Session = AuthenticatedUser;

export interface LoginInput {
  readonly email: string;
  readonly password: string;
}

/** `GET /api/auth/me` — the current session, or 401. Drops `memberships`; nothing here reads it yet. */
export async function fetchSession(signal?: AbortSignal): Promise<Session> {
  const response = await apiRequest(
    '/api/auth/me',
    meResponseSchema,
    signal === undefined ? {} : { signal },
  );
  return response.user;
}

/** `POST /api/auth/login` — returns the new session on success, 401 on bad credentials. */
export async function login(input: LoginInput): Promise<Session> {
  const response = await apiRequest('/api/auth/login', authSessionResponseSchema, {
    method: 'POST',
    body: input,
  });
  return response.user;
}

/**
 * `POST /api/auth/logout` — always succeeds from the client's point of view.
 *
 * The response body is ignored (`z.unknown()`) rather than asserted: a logout that returns 204,
 * `{}`, or the deleted session id are all fine, and failing the sign-out because the server's
 * goodbye had an unexpected shape would strand a user in a session they asked to end.
 */
export function logout(): Promise<unknown> {
  return apiRequest('/api/auth/logout', z.unknown(), { method: 'POST' });
}
